/**
 * Property detail — editable beteckning/kommun/beskrivning, the
 * fastighet's gatuadresser (T-10 / ADR-0047), and the contracts linked
 * to it.
 *
 * A fastighet can be a connected building spanning several street
 * addresses. The address list below is the source of truth; the first
 * row is the primary (mirrored into the property's address cache and
 * used as the default when a contract links this property). The unit
 * catalog used to live here but has no consumer since contracts stopped
 * tracking apartments.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { tenantApi, type ContractListRow, type PropertyAddress } from '../api.js';
import { useTenantSession } from '../auth-context.js';
import { useInvalidate, useTenantQuery } from '../query.js';
import { ContractsTabbedList } from '../components/ContractsTabbedList.js';
import { usePageTitle } from '../use-page-title.js';

const STATUS_LABEL: Record<ContractListRow['status'], string> = {
  draft: 'Utkast',
  pending: 'Väntar',
  in_review: 'Granskas',
  awaiting_manual_signature: 'Väntar på underskrift',
  sent: 'Skickat',
  viewed: 'Visat',
  awaiting_signature: 'Väntar på signering',
  signed: 'Signerat',
  archived: 'Arkiverat',
  cancelled: 'Avbrutet',
};

const STATUS_CLASS: Record<ContractListRow['status'], string> = {
  draft: 'bg-stone-100 text-stone-700',
  pending: 'bg-stone-100 text-stone-700',
  in_review: 'bg-purple-100 text-purple-800',
  awaiting_manual_signature: 'bg-amber-100 text-amber-900',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-blue-100 text-blue-800',
  awaiting_signature: 'bg-amber-100 text-amber-900',
  signed: 'bg-green-100 text-green-800',
  archived: 'bg-stone-200 text-stone-600',
  cancelled: 'bg-red-100 text-red-800',
};

/** Editable address row (no id — the server rewrites the whole list). */
type AddrDraft = { street: string; postalCode: string; ort: string };

function toDraft(a: PropertyAddress): AddrDraft {
  return { street: a.street, postalCode: a.postalCode ?? '', ort: a.ort ?? '' };
}

function addrsEqual(a: AddrDraft[], b: AddrDraft[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (x, i) =>
      x.street.trim() === b[i]!.street.trim() &&
      x.postalCode.trim() === b[i]!.postalCode.trim() &&
      x.ort.trim() === b[i]!.ort.trim(),
  );
}

export function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { token } = useTenantSession();
  const navigate = useNavigate();
  const invalidate = useInvalidate();

  const detailQ = useTenantQuery(
    'properties.get',
    (pid) => tenantApi.properties.get(pid as string),
    id ? [id] : [],
  );
  const property = detailQ.data?.property ?? null;
  const serverAddresses = detailQ.data?.addresses ?? null;
  const contracts: ContractListRow[] | null = detailQ.data?.contracts ?? null;

  usePageTitle(property ? property.name : null);

  const [name, setName] = useState('');
  const [kommun, setKommun] = useState('');
  const [description, setDescription] = useState('');
  const [addrs, setAddrs] = useState<AddrDraft[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Hydrate the editable fields from the server snapshot. Only fires when
  // the underlying property *id* changes, so user edits aren't clobbered
  // by background revalidation.
  useEffect(() => {
    if (!property) return;
    setName(property.name);
    setKommun(property.kommun ?? '');
    setDescription(property.description ?? '');
    const initial = (serverAddresses ?? []).map(toDraft);
    // Pre-address-list properties may carry only the legacy street cache;
    // seed a single row so the editor has something to show.
    if (initial.length === 0 && property.street) {
      initial.push({
        street: property.street,
        postalCode: property.postalCode ?? '',
        ort: property.ort ?? '',
      });
    }
    setAddrs(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property?.id]);

  const queryErr = err ?? (detailQ.error ? (detailQ.error as Error).message : null);

  if (!property) {
    return (
      <section className="space-y-3">
        <Link to="/properties" className="text-xs text-stone-500 underline">
          ← Fastigheter
        </Link>
        {queryErr ? (
          <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {queryErr}
          </div>
        ) : (
          <div className="text-sm text-stone-400">loading…</div>
        )}
      </section>
    );
  }

  const originalAddrs = (serverAddresses ?? []).map(toDraft);
  const propsDirty =
    name !== property.name ||
    kommun !== (property.kommun ?? '') ||
    description !== (property.description ?? '');
  const addrsDirty = !addrsEqual(addrs, originalAddrs);
  const dirty = propsDirty || addrsDirty;

  const setAddr = (i: number, patch: Partial<AddrDraft>) =>
    setAddrs((prev) => prev.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const addAddr = () =>
    // Pre-fill postnr/ort from the primary row — entrances of one building
    // virtually always share them (still editable for the rare exception).
    setAddrs((prev) => [
      ...prev,
      {
        street: '',
        postalCode: prev[0]?.postalCode ?? '',
        ort: prev[0]?.ort ?? '',
      },
    ]);
  const removeAddr = (i: number) => setAddrs((prev) => prev.filter((_, j) => j !== i));
  const makePrimary = (i: number) =>
    setAddrs((prev) => {
      if (i === 0) return prev;
      const next = [...prev];
      const [row] = next.splice(i, 1);
      next.unshift(row!);
      return next;
    });

  const cleanedAddrs = addrs
    .map((a) => ({ ...a, street: a.street.trim() }))
    .filter((a) => a.street.length > 0);

  const save = async () => {
    if (!token || !id) return;
    setBusy(true);
    setErr(null);
    try {
      if (propsDirty) {
        await tenantApi.properties.update({
          id,
          name: name.trim(),
          kommun: kommun.trim() || null,
          description: description.trim() || null,
        });
      }
      if (addrsDirty && cleanedAddrs.length > 0) {
        await tenantApi.properties.addresses.set({
          property_id: id,
          addresses: cleanedAddrs.map((a) => ({
            street: a.street,
            postal_code: a.postalCode.trim() || null,
            ort: a.ort.trim() || null,
          })),
        });
      }
      await Promise.all([
        invalidate('properties.get', id),
        invalidate('properties.list'),
      ]);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const resetForm = () => {
    setName(property.name);
    setKommun(property.kommun ?? '');
    setDescription(property.description ?? '');
    setAddrs(originalAddrs);
  };

  const archiveProperty = async () => {
    if (!token || !id) return;
    if (!confirm(`Arkivera "${property.name}"?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.properties.archive({ id });
      await invalidate('properties.list');
      navigate('/properties');
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <section className="space-y-4">
      <Link to="/properties" className="text-xs text-stone-500 underline">
        ← Fastigheter
      </Link>

      {queryErr && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">
          {queryErr}
        </div>
      )}

      <div className="space-y-3 rounded border border-stone-200 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-stone-500">Fastighet</h2>
          <button
            type="button"
            onClick={archiveProperty}
            disabled={busy}
            className="text-xs text-red-600 hover:underline disabled:opacity-50"
          >
            Arkivera
          </button>
        </div>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="block text-xs font-medium text-stone-600">Beteckning</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="t.ex. Klockaren 5"
              className="w-full rounded border border-stone-300 px-2 py-1.5 text-sm"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-medium text-stone-600">
                Adresser
              </label>
              <span className="text-[10px] text-stone-400">
                Första adressen är förvald (primär)
              </span>
            </div>
            {addrs.length === 0 ? (
              <p className="text-xs text-stone-400">Inga adresser ännu.</p>
            ) : (
              <ul className="space-y-2">
                {addrs.map((a, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 rounded border border-stone-200 bg-stone-50 p-2"
                  >
                    <button
                      type="button"
                      title={i === 0 ? 'Primär adress' : 'Gör till primär'}
                      onClick={() => makePrimary(i)}
                      disabled={i === 0}
                      className={`mt-1 shrink-0 ${
                        i === 0 ? 'text-green-600' : 'text-stone-300 hover:text-stone-500'
                      }`}
                    >
                      <GripVertical className="h-4 w-4" />
                    </button>
                    <div className="flex-1 space-y-2">
                      <input
                        value={a.street}
                        onChange={(e) => setAddr(i, { street: e.target.value })}
                        placeholder="Gatuadress, t.ex. Ångvägen 3"
                        className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
                      />
                      <div className="grid grid-cols-[140px_1fr] gap-2">
                        <input
                          value={a.postalCode}
                          onChange={(e) => setAddr(i, { postalCode: e.target.value })}
                          placeholder="114 44"
                          className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
                        />
                        <input
                          value={a.ort}
                          onChange={(e) => setAddr(i, { ort: e.target.value })}
                          placeholder="Stockholm"
                          className="w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      title="Ta bort adress"
                      onClick={() => removeAddr(i)}
                      className="mt-1 shrink-0 text-stone-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={addAddr}
              className="flex items-center gap-1 text-xs text-stone-600 hover:underline"
            >
              <Plus className="h-3.5 w-3.5" /> Lägg till adress
            </button>
          </div>

          <div className="space-y-1">
            <label className="block text-xs font-medium text-stone-600">Kommun</label>
            <input
              value={kommun}
              onChange={(e) => setKommun(e.target.value)}
              placeholder="t.ex. Stockholm"
              className="w-full rounded border border-stone-300 px-2 py-1.5 text-sm"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-stone-600">Beskrivning</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded border border-stone-300 px-2 py-1.5 text-sm"
            />
          </div>
        </div>
        {dirty && (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={resetForm}
              className="rounded px-3 py-1.5 text-xs font-medium text-stone-600"
            >
              Avbryt
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy || name.trim().length === 0}
              className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {busy ? 'Sparar…' : 'Spara'}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3 rounded border border-stone-200 p-4">
        <h2 className="text-sm font-medium text-stone-500">Avtal kopplade till fastigheten</h2>

        {contracts === null ? (
          <div className="text-sm text-stone-400">loading…</div>
        ) : contracts.length === 0 ? (
          <div className="rounded border border-dashed border-stone-300 p-4 text-center text-xs text-stone-500">
            Inga avtal är kopplade till den här fastigheten ännu. Skapa ett från{' '}
            <Link to="/templates" className="underline">
              en mall
            </Link>{' '}
            och länka till denna fastighet i editorn.
          </div>
        ) : (
          <ContractsTabbedList
            contracts={contracts}
            renderRow={(c) => (
              <li key={c.id} className="hover:bg-stone-50">
                <Link to={`/contracts/${c.id}/edit`} className="block p-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-medium">{c.title}</div>
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${
                        STATUS_CLASS[c.status]
                      }`}
                    >
                      {STATUS_LABEL[c.status]}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-stone-500">
                    {c.templateName} · senast ändrad{' '}
                    {new Date(c.updatedAt).toLocaleString('sv-SE')}
                  </div>
                </Link>
              </li>
            )}
          />
        )}
      </div>
    </section>
  );
}
